import {
    ButtonOutlinedPrimary,
    ButtonPrimary,
    Colors,
    Icon,
    RecognitionCarousel,
    Spinner,
} from '@amelio/ui-library';
import { useId } from 'react';

import { ReceivedRecognitionCard } from './ReceivedRecognitionCard';
import { openRecognitionFeed } from './recognitionFeed';

import type { ReceivedRecognition, ReceivedRecognitions } from '@/types/recognition';

import { GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import { useAmelioTranslations } from '@/hooks';
import { usePortalRecognitions } from '@/pages/EmployeePortal/hooks/usePortalReads';
import { useViewedEmployee } from '@/pages/EmployeePortal/hooks/useViewedEmployee';
import {
    isEngagementUnavailableError,
    isRecognitionModuleInactiveError,
} from '@/utils/recognitionErrors';

const DATA_TEST_ID = 'employee-portal-recognitions';

/**
 * The "Reconnaissance" section of the employee portal: the ten recognitions the employee last
 * received, shuffled, one at a time in a carousel. On the reader's own portal a card leads to the
 * Engagement Recognition feed, and so does the invitation shown to an employee who has not been
 * recognized yet.
 *
 * On someone else's portal neither leads anywhere: the feed path Engagement returns is a fixed
 * route, so it opens the reader's own feed -- where the employee's recognitions are not. A link
 * that lands somewhere other than where it says is worse than no link, so the cards are drawn
 * without one and the invitation stands as a sentence.
 *
 * The section disappears altogether for a company whose plan has no Recognition module — that is an
 * absence, not an empty list, and not a failure either.
 */
export const RecognitionsSection = () => {
    const { t } = useAmelioTranslations();
    const titleId = useId();
    const { data, isLoading, isError, error, refetch } = usePortalRecognitions();
    const { viewedEmployeeId } = useViewedEmployee();

    if (isRecognitionModuleInactiveError(error)) {
        return null;
    }

    return (
        <section
            aria-labelledby={titleId}
            className={`${GLASS_SURFACE} flex flex-col gap-6 p-6 sm:p-10`}
            data-testid={DATA_TEST_ID}
        >
            <header className={'flex items-start gap-3'}>
                <Icon
                    name={'lucide-heart'}
                    size={30}
                    colorClassName={'text-navy'}
                    className={'shrink-0'}
                    ariaHidden
                />

                <div>
                    <h2
                        id={titleId}
                        className={'text-navy text-[20px] leading-tight font-medium'}
                    >
                        {t('employeePortalRecognitions.title')}
                    </h2>

                    <p className={'text-base-400 text-[14px] font-medium'}>
                        {t('employeePortalRecognitions.subtitle')}
                    </p>
                </div>
            </header>

            <RecognitionsBody
                isFeedReachable={viewedEmployeeId === undefined}
                recognitions={data}
                isLoading={isLoading}
                isUnavailable={isEngagementUnavailableError(error)}
                isError={isError}
                onRetry={() => void refetch()}
            />
        </section>
    );
};

interface RecognitionsBodyProps {
    /** The answer, once it has come. */
    recognitions?: ReceivedRecognitions;
    /** Whether the feed the recognitions link to is the reader's own, and so worth offering. */
    isFeedReachable: boolean;
    isLoading: boolean;
    /** Engagement itself could not be reached: a passing failure, worth offering a retry. */
    isUnavailable: boolean;
    isError: boolean;
    onRetry: () => void;
}

/** What the section shows below its header, which is never "nothing yet" before the answer. */
const RecognitionsBody = ({
    recognitions,
    isFeedReachable,
    isLoading,
    isUnavailable,
    isError,
    onRetry,
}: RecognitionsBodyProps) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return (
            <div
                className={'flex justify-center p-8'}
                data-testid={`${DATA_TEST_ID}-loading`}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    if (isUnavailable) {
        return (
            <div className={'flex flex-col items-center gap-4 py-6 text-center'}>
                <p role={'alert'}>{t('employeePortalRecognitions.unavailable')}</p>

                <ButtonOutlinedPrimary
                    size={'sm'}
                    fitContent
                    onClick={onRetry}
                    dataTestId={`${DATA_TEST_ID}-retry`}
                >
                    {t('employeePortalRecognitions.retry')}
                </ButtonOutlinedPrimary>
            </div>
        );
    }

    if (isError || !recognitions) {
        return (
            <p
                role={'alert'}
                className={'text-error text-[14px]'}
            >
                {t('employeePortalRecognitions.loadError')}
            </p>
        );
    }

    const { recognitions: received, feedPath } = recognitions;

    if (received.length === 0) {
        return (
            <div className={'flex flex-col items-center gap-4 py-6 text-center'}>
                <p className={'text-base-400 max-w-md text-[14px]'}>
                    {t('employeePortalRecognitions.empty')}
                </p>

                {isFeedReachable && (
                    <ButtonPrimary
                        size={'sm'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'lucide-heart'}
                                size={'sm'}
                                ariaHidden
                            />
                        }
                        onClick={() => openRecognitionFeed(feedPath)}
                        dataTestId={`${DATA_TEST_ID}-recognize`}
                    >
                        {t('employeePortalRecognitions.recognizeColleague')}
                    </ButtonPrimary>
                )}
            </div>
        );
    }

    return (
        <RecognitionCarousel
            items={received}
            getKey={(recognition: ReceivedRecognition) => recognition.id}
            renderItem={(recognition: ReceivedRecognition) => (
                <ReceivedRecognitionCard
                    recognition={recognition}
                    onOpenFeed={isFeedReachable ? () => openRecognitionFeed(feedPath) : undefined}
                />
            )}
            labels={{
                previous: t('employeePortalRecognitions.previous'),
                next: t('employeePortalRecognitions.next'),
                // Under the card, a caption stands where the counter used to: the position stays
                // in the announced region, where it is the carousel's only cue of where one is.
                position: (position, total) => (
                    <>
                        <span
                            aria-hidden={true}
                            className={'text-base-400 font-normal italic'}
                        >
                            {t('employeePortalRecognitions.carouselCaption')}
                        </span>

                        <span className={'sr-only'}>
                            {t('employeePortalRecognitions.position', { position, total })}
                        </span>
                    </>
                ),
            }}
            ariaLabel={t('employeePortalRecognitions.carousel')}
            dataTestId={`${DATA_TEST_ID}-carousel`}
            // The carousel pins its arrows to its own edges, and the card it holds is capped at
            // 560px: left to the full width of the section, the arrows would drift away from it.
            className={'w-full max-w-[720px] self-center'}
        />
    );
};
